/**
 * The database has no colour column, so the accent is derived from the
 * pavilion's display order: first pavilion lime, second cyan, and so on,
 * matching the design. Reordering pavilions in the admin therefore recolours
 * them, which is the accepted trade-off for not storing colour as data.
 */
export const PAVILION_ACCENTS = ['trade', 'tech', 'finance', 'services'] as const;

export type PavilionAccent = (typeof PAVILION_ACCENTS)[number];

// Tailwind scans source files for complete class names, so accent classes must
// appear as literals here rather than being built from a template string.
const TEXT: Record<PavilionAccent, string> = {
  trade: 'text-pav-trade',
  tech: 'text-pav-tech',
  finance: 'text-pav-finance',
  services: 'text-pav-services',
};

const BACKGROUND: Record<PavilionAccent, string> = {
  trade: 'bg-pav-trade',
  tech: 'bg-pav-tech',
  finance: 'bg-pav-finance',
  services: 'bg-pav-services',
};

const VARIABLE: Record<PavilionAccent, string> = {
  trade: 'var(--color-pav-trade)',
  tech: 'var(--color-pav-tech)',
  finance: 'var(--color-pav-finance)',
  services: 'var(--color-pav-services)',
};

/**
 * Maps a pavilion's position onto the palette, wrapping when a fair has more
 * pavilions than there are accents so every pavilion still gets a colour.
 */
export function accentFor(position: number): PavilionAccent {
  const index = Number.isFinite(position) ? Math.max(0, Math.trunc(position)) : 0;

  return PAVILION_ACCENTS[index % PAVILION_ACCENTS.length] ?? 'trade';
}

export function accentText(accent: PavilionAccent): string {
  return TEXT[accent];
}

export function accentBackground(accent: PavilionAccent): string {
  return BACKGROUND[accent];
}

export function accentVariable(accent: PavilionAccent): string {
  return VARIABLE[accent];
}
